import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Edit3, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/testimonials");
        setItems(data);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing({ name: "", city: "", rating: 5, text: "", active: true, order: items.length }); setOpen(true); };
    const openEdit = (t) => { setEditing({ ...t }); setOpen(true); };

    const save = async () => {
        try {
            if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, editing);
            else await api.post("/admin/testimonials", editing);
            toast.success("Testemunho guardado");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Eliminar?")) return;
        await api.delete(`/admin/testimonials/${id}`); load();
    };

    return (
        <div className="space-y-6" data-testid="admin-testimonials">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">Testemunhos de clientes</h1>
                    <p className="text-white/60">Aparecem na secção &ldquo;Testemunhos&rdquo; da landing quando ativos.</p>
                </div>
                <Button onClick={openNew} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="add-testimonial">
                    <Plus className="w-4 h-4 mr-2" /> Novo testemunho
                </Button>
            </div>

            {items.length === 0 ? (
                <div className="ab-card p-12 text-center text-white/60">
                    Ainda não há testemunhos. Adicione o primeiro para começar a mostrar prova social.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {items.map((t) => (
                        <div key={t.id} className="ab-card p-6" data-testid={`testimonial-item-${t.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="flex mb-2">
                                        {[...Array(t.rating)].map((_, s) => <Star key={s} className="w-4 h-4 text-[#C9A961]" fill="#C9A961" />)}
                                    </div>
                                    <div className="font-heading font-bold text-white">{t.name}</div>
                                    <div className="text-xs text-white/50">{t.city}</div>
                                </div>
                                <div className="flex gap-1">
                                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="text-white/60 hover:bg-[#1F1F1F]"><Edit3 className="w-4 h-4" /></Button>
                                    <Button size="icon" variant="ghost" onClick={() => remove(t.id)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                                </div>
                            </div>
                            <p className="text-sm text-white/70 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                            {!t.active && <div className="mt-3 text-xs uppercase tracking-widest text-amber-400">Oculto</div>}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing?.id ? "Editar testemunho" : "Novo testemunho"}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Nome</Label>
                                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="testimonial-name" />
                                </div>
                                <div>
                                    <Label>Cidade</Label>
                                    <Input value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="testimonial-city" />
                                </div>
                            </div>
                            <div>
                                <Label>Avaliação ({editing.rating}/5)</Label>
                                <div className="flex gap-1 mt-1">
                                    {[1, 2, 3, 4, 5].map((r) => (
                                        <button key={r} onClick={() => setEditing({ ...editing, rating: r })}>
                                            <Star className="w-6 h-6 text-[#C9A961]" fill={r <= editing.rating ? "#C9A961" : "transparent"} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>Testemunho</Label>
                                <Textarea rows={4} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="testimonial-text" />
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> <Label>Ativo</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="save-testimonial"><Save className="w-4 h-4 mr-2" />Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
